import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Statistics.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:1337';

const STATUS_LABELS = {
  pending: 'In behandeling',
  paid: 'Betaald',
  processing: 'Verwerking',
  shipped: 'Verzonden',
  completed: 'Voltooid',
  cancelled: 'Geannuleerd',
};

const STATUS_COLORS = {
  pending: '#f6ad55',
  paid: '#68d391',
  processing: '#76e4f7',
  shipped: '#00d9ff',
  completed: '#00ff88',
  cancelled: '#fc8181',
};

const DAY_LABELS = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

const fetchCount = async (endpoint, token) => {
  const res = await fetch(`${API_URL}/api/${endpoint}?pagination[pageSize]=1`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return 0;
  const data = await res.json();
  return data.meta?.pagination?.total ?? 0;
};

const startOf = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const Statistics = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counts, setCounts] = useState({
    projects: 0, services: 0, products: 0, teamMembers: 0, orders: 0,
  });
  const [orders, setOrders] = useState([]);
  const [pageViews, setPageViews] = useState([]);

  useEffect(() => {
    loadStats();
  }, [token]);

  const loadStats = async () => {
    setLoading(true);
    setError('');
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const since = thirtyDaysAgo.toISOString();

      const [projects, services, products, teamMembers, ordersRes, viewsRes] = await Promise.all([
        fetchCount('projects', token),
        fetchCount('services', token),
        fetchCount('products', token),
        fetchCount('team-members', token),
        fetch(`${API_URL}/api/orders?pagination[pageSize]=100&sort=createdAt:desc`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(
          `${API_URL}/api/page-views?filters[createdAt][$gte]=${since}&pagination[pageSize]=1000&sort=createdAt:desc`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      if (!ordersRes.ok) throw new Error('Kan bestellingen niet laden');
      const ordersData = await ordersRes.json();
      const allOrders = ordersData.data || [];

      const allViews = viewsRes.ok ? (await viewsRes.json()).data || [] : [];

      setCounts({
        projects, services, products, teamMembers,
        orders: ordersData.meta?.pagination?.total ?? allOrders.length,
      });
      setOrders(allOrders);
      setPageViews(allViews);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Order derived stats
  const revenue = orders
    .filter((o) => ['paid', 'completed'].includes(o.attributes?.status || o.status))
    .reduce((sum, o) => sum + (o.attributes?.orderTotal ?? o.orderTotal ?? 0), 0);

  const statusCounts = orders.reduce((acc, o) => {
    const s = o.attributes?.status || o.status || 'pending';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Visitor derived stats
  const now = new Date();
  const todayStart = startOf(now);
  const weekStart = startOf(new Date(now));
  weekStart.setDate(weekStart.getDate() - 6);

  const viewsToday = pageViews.filter(
    (v) => new Date(v.attributes?.createdAt || v.createdAt) >= todayStart
  ).length;

  const viewsWeek = pageViews.filter(
    (v) => new Date(v.attributes?.createdAt || v.createdAt) >= weekStart
  ).length;

  // Last 7 days bar chart data
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = startOf(new Date(now));
    d.setDate(d.getDate() - (6 - i));
    const next = new Date(d);
    next.setDate(next.getDate() + 1);
    return {
      label: DAY_LABELS[d.getDay()],
      count: pageViews.filter((v) => {
        const t = new Date(v.attributes?.createdAt || v.createdAt);
        return t >= d && t < next;
      }).length,
    };
  });
  const maxDay = Math.max(...last7.map((d) => d.count), 1);

  // Top pages
  const pageCounts = pageViews.reduce((acc, v) => {
    const p = v.attributes?.path || v.path || '/';
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});
  const topPages = Object.entries(pageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  if (loading) return <div className="stats-page"><p className="loading">Statistieken worden geladen...</p></div>;

  return (
    <div className="stats-page">
      <h1>Statistieken</h1>

      {error && <div className="error-message">{error}</div>}

      {/* Content counts */}
      <section className="stats-section">
        <h2>Overzicht</h2>
        <div className="stat-cards">
          <StatCard label="Projecten" value={counts.projects} icon="📁" />
          <StatCard label="Diensten" value={counts.services} icon="⚙️" />
          <StatCard label="Producten" value={counts.products} icon="🛍️" />
          <StatCard label="Teamleden" value={counts.teamMembers} icon="👤" />
          <StatCard label="Bestellingen" value={counts.orders} icon="📦" />
          <StatCard label="Omzet (betaald)" value={`€ ${(revenue / 100).toFixed(2)}`} icon="💶" accent />
        </div>
      </section>

      {/* Visitor stats */}
      <section className="stats-section">
        <h2>Bezoekers (afgelopen 30 dagen)</h2>
        <div className="stat-cards" style={{ marginBottom: '1.5rem' }}>
          <StatCard label="Totaal paginaweergaven" value={pageViews.length} icon="👁️" />
          <StatCard label="Vandaag" value={viewsToday} icon="📅" />
          <StatCard label="Afgelopen 7 dagen" value={viewsWeek} icon="📆" />
        </div>

        {/* 7-day bar chart */}
        <div className="bar-chart">
          {last7.map((day, i) => (
            <div key={i} className="bar-col">
              <span className="bar-count">{day.count > 0 ? day.count : ''}</span>
              <div className="bar-wrap">
                <div
                  className="bar-fill"
                  style={{ height: `${Math.round((day.count / maxDay) * 100)}%` }}
                />
              </div>
              <span className="bar-label">{day.label}</span>
            </div>
          ))}
        </div>

        {/* Top pages */}
        {topPages.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <p style={{ color: '#aaa', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.75rem' }}>
              Populairste pagina's
            </p>
            <div className="status-bars">
              {topPages.map(([path, count]) => {
                const pct = Math.round((count / (topPages[0][1] || 1)) * 100);
                return (
                  <div key={path} className="status-bar-row">
                    <span className="status-label" style={{ color: '#ccc', fontWeight: 400, fontFamily: 'monospace' }}>
                      {path}
                    </span>
                    <div className="status-bar-track">
                      <div className="status-bar-fill" style={{ width: `${pct}%`, background: '#00d9ff' }} />
                    </div>
                    <span className="status-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {pageViews.length === 0 && (
          <p className="empty">Nog geen bezoekers geregistreerd — data verschijnt zodra er bezoekers zijn</p>
        )}
      </section>

      {/* Orders by status */}
      <section className="stats-section">
        <h2>Bestellingen per status</h2>
        {Object.keys(statusCounts).length === 0 ? (
          <p className="empty">Geen bestellingen</p>
        ) : (
          <div className="status-bars">
            {Object.entries(statusCounts).map(([status, count]) => {
              const pct = Math.round((count / orders.length) * 100);
              return (
                <div key={status} className="status-bar-row">
                  <span className="status-label" style={{ color: STATUS_COLORS[status] || '#aaa' }}>
                    {STATUS_LABELS[status] || status}
                  </span>
                  <div className="status-bar-track">
                    <div className="status-bar-fill" style={{ width: `${pct}%`, background: STATUS_COLORS[status] || '#aaa' }} />
                  </div>
                  <span className="status-count">{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Recent orders */}
      <section className="stats-section">
        <h2>Recente bestellingen</h2>
        {orders.length === 0 ? (
          <p className="empty">Geen bestellingen</p>
        ) : (
          <div className="recent-orders">
            <table>
              <thead>
                <tr>
                  <th>Klant</th>
                  <th>E-mail</th>
                  <th>Totaal</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 5).map((order) => {
                  const a = order.attributes || order;
                  const status = a.status || 'pending';
                  return (
                    <tr key={order.id}>
                      <td>{a.customer_name || '-'}</td>
                      <td>{a.customer_email || '-'}</td>
                      <td>€ {((a.orderTotal ?? 0) / 100).toFixed(2)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            background: STATUS_COLORS[status] + '22',
                            color: STATUS_COLORS[status] || '#aaa',
                            border: `1px solid ${STATUS_COLORS[status] || '#aaa'}`,
                          }}
                        >
                          {STATUS_LABELS[status] || status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const StatCard = ({ label, value, icon, accent }) => (
  <div className={`stat-card ${accent ? 'stat-card--accent' : ''}`}>
    <span className="stat-icon">{icon}</span>
    <span className="stat-value">{value}</span>
    <span className="stat-label">{label}</span>
  </div>
);

export default Statistics;
